import { useTranslation } from "react-i18next";
import {
    TEXT_FIELD_TYPE,
    DATE_RANGE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE
} from "../../helpers/constants";
import { formatCurrency } from "../../components/common/PriceDisplay";
import { AgreementPassportButton } from "../AgreementsPage/AgreementPassportDialog/AgreementPassportButton";
import {Typography} from "@mui/material";
import { AddContactForAgreementOverduePaymentButton } from "./AddContactForAgreementOverduePaymentButton";
import { PreviewContactForAgreementOverduePaymentButton } from "./PreviewContractForAgreementOverduePaymentButton";
import { useInvestmentSelectList } from "../../services/productsService";

export const AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME = "agreement_overdue_payment_table";

export const useAgreementOverduePaymentTableConfig = () => {
    const { t } = useTranslation();

    const { data: investments } = useInvestmentSelectList();

    return [
        {
            name: "agreement_nr",
            getValueCallback: (rowData) => rowData.agreement_nr,
            label: t("table.agreements.agreementNr"),
            filterType: TEXT_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "agreement_notarial_dead",
            getValueCallback: (rowData) => rowData.agreement_notarial_dead,
            label: t("table.agreements.agreementNotarialDeed"),
            filterType: TEXT_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "agreement_customer",
            getValueCallback: (rowData) => rowData.agreement_customer,
            label: t("table.agreements.customers"),
            filterType: TEXT_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "agreement_company",
            getValueCallback: (rowData) => rowData.agreement_company,
            label: t("table.agreements.companyNames"),
            filterType: TEXT_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "agreement_product",
            getValueCallback: (rowData) => rowData.agreement_product,
            label: t("table.agreements.productNumbers"),
            filterType: TEXT_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "agreement_invest",
            getValueCallback: (rowData) => rowData.agreement_invest?.investment_name_sales,
            label: t("table.agreements.investment"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                optionLabelKey: "investment_name_sales",
                options: investments,
                isObjectOption: true,
                disableClearable: true,
                multiple: false,
                style: { minWidth: "120px" }
            },
        },
        {
            name: "overdue_days",
            getValueCallback: (rowData) => rowData.overdue_days,
            label: t("table.agreements.overdueDays"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "agreement_passport",
            getValueCallback: (rowData) => <AgreementPassportButton agreement={{id:rowData.agreement_id}} />,
            label: t("table.agreements.agreementPassport"),
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
            excludedFromExport: true,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "contact_action",
            getValueCallback: (rowData) => (rowData.contact_id ? 
                    <Typography color={"#068e06"}>{t("table.agreements.contactDone")}</Typography> :
                    <AddContactForAgreementOverduePaymentButton agreementId={rowData.agreement_id} agreementOverduePaymentId={rowData.id} overduePaymentBalance={rowData.agreement_overdue_payment_balance}/>
            ),
            label: t("table.agreements.contactAction"),
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
            excludedFromExport: true,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "agreement_overdue_payment_balance",
            getValueCallback: (rowData) => formatCurrency(rowData.agreement_overdue_payment_balance),
            label: t("table.agreements.agreementOverduePaymentBalance"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "contact_date",
            getValueCallback: (rowData) => rowData.contact_date,
            label: t("table.agreements.contactDate"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "contact_seller",
            getValueCallback: (rowData) => rowData.contact_seller,
            label: t("table.agreements.contactSeller"),
            filterType: TEXT_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "contact_note",
            getValueCallback: (rowData) => rowData.contact_note,
            label: t("table.agreements.contactNote"),
            filterType: TEXT_FIELD_TYPE,
            headerSx: { whiteSpace: "break-spaces" },
        },
        {
            name: "contact_preview",
            headerSx: { whiteSpace: "break-spaces" },
            getValueCallback: (rowData) => (rowData.contact_id ? 
                    <PreviewContactForAgreementOverduePaymentButton 
                        contactId={rowData.contact_id}
                        personId={rowData.contact_row_data.contact_person}
                        agreementOverduePaymentId={rowData.id}
                        contactData={{
                            way: rowData.contact_row_data.contact_way,
                            subject: rowData.contact_row_data.contact_subject?.contsubj_name_eng,
                            date: rowData.contact_row_data.contact_date,
                            note: rowData.contact_row_data.contact_note,
                            productsIds: rowData.contact_row_data.product_ids,
                            ...(rowData.contact_row_data.offer_id && { offerId: rowData.contact_row_data.offer_id }),
                        }}
                    />:
                    null
            ),
            // getValueCallback: (rowData) => (rowData.contact_id ? 
            //         <ContactPreviewButton 
            //             personId={rowData.contact_row_data.contact_person}
            //             rowData={{
            //                 way: rowData.contact_row_data.contact_way,
            //                 subject: rowData.contact_row_data.contact_subject?.contsubj_name_eng,
            //                 date: rowData.contact_row_data.contact_date,
            //                 note: rowData.contact_row_data.contact_note,
            //                 productsIds: rowData.contact_row_data.product_ids,
            //                 ...(rowData.contact_row_data.offer_id && { offerId: rowData.contact_row_data.offer_id }),
            //                 agreementOverduePaymentId: rowData.agreement_overdue_payment_Id,
            //                 rawPayments: rowData.raw_payments,

            //             }}
            //         />:
            //         null
            // ),
            label: t("table.agreements.contactPreview"),
            headerSx: { whiteSpace: "break-spaces" },
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
            excludedFromExport: true,
        },
    ];
};
